"use client";
import { useState, type ChangeEvent, type MouseEvent } from "react";
import { type Vacancy } from "@/data/vacancies";
import VacancyHero from "@/sections/vacancies/vacancyHero";
import { Box, Stack, Typography, Divider, InputBase, Checkbox } from "@mui/material";
import SideTabbedButton from "@/components/generic/SideTabbedButton";
import UploadField from "@/components/generic/uploadField";
import theme from "@/theme/theme";
import Image from "next/image";
import Link from "next/link";
import Footer from "@/components/layout/footer";

import arrow from "@/assets/images/icons/arrow.webp"

/**
 * Explicit labels for the "About this role" key facts. Keyed on the fields of
 * Vacancy["aboutThisRole"]. A camelCase split is not used here because it
 * renders "howToApply" as "How To Apply"; every key gets its label spelled out.
 */
const ABOUT_THIS_ROLE_LABELS: Record<string, string> = {
    position: "Position",
    location: "Location",
    type: "Type",
    compensation: "Compensation",
    engagement: "Engagement",
    availability: "Availability",
    status: "Status",
    durationAndHours: "Duration and hours",
    eligibility: "Eligibility",
    supervision: "Supervision",
    outcome: "Outcome",
    growth: "Growth",
    howToApply: "How to apply",
};

/** Falls back to a sentence-cased camelCase split for any key without an explicit label. */
function aboutThisRoleLabel(key: string): string {
    const explicit = ABOUT_THIS_ROLE_LABELS[key];
    if (explicit) return explicit;
    const words = key.replace(/([A-Z])/g, " $1").trim().toLowerCase();
    return words.charAt(0).toUpperCase() + words.slice(1);
}



/**
 * Renders a single vacancy and its application form.
 *
 * The vacancy is resolved on the server (see src/app/vacancies/[id]/page.tsx)
 * so the route can carry per-posting metadata. `vacancy` is undefined for a
 * slug that no longer exists, which renders the "listing taken down" card.
 */
export default function VacancyDetail({ vacancy }: { vacancy: Vacancy | undefined }) {

    const [fullName, setFullName] = useState("");
    const [emailAddress, setEmailAddress] = useState("");
    const [linkedinProfile, setLinkedinProfile] = useState("");
    const [portfolioUrl, setPortfolioUrl] = useState("");
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [coverLetterFile, setCoverLetterFile] = useState<File | null>(null);
    const [agreeToProcessing, setAgreeToProcessing] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitOk, setSubmitOk] = useState(false);

    const canSubmit =
        fullName.trim().length > 0 &&
        emailAddress.trim().length > 0 &&
        agreeToProcessing &&
        !isSubmitting;

    async function onSubmit() {
        setSubmitError(null);
        setSubmitOk(false);
        setIsSubmitting(true);

        try {
            const formData = new FormData();
            formData.append("fullName", fullName);
            formData.append("email", emailAddress);
            formData.append("linkedin", linkedinProfile);
            formData.append("portfolio", portfolioUrl);
            formData.append("agreeToProcessing", String(agreeToProcessing));

            if (resumeFile) formData.append("resumeFile", resumeFile);
            if (coverLetterFile) formData.append("coverLetterFile", coverLetterFile);

            const res = await fetch(`/api/vacancies/${vacancy?.id ?? ""}/apply`, {
                method: "POST",
                body: formData,
            });

            const json = (await res.json()) as { ok: boolean; error?: string };

            if (!res.ok || !json.ok) {
                throw new Error(json.error ?? "Failed to submit.");
            }

            setSubmitOk(true);
        } catch (e) {
            setSubmitError(e instanceof Error ? e.message : "Failed to submit.");
        } finally {
            setIsSubmitting(false);
        }
    }

    if (!vacancy) {
        return (
            <Stack width={"100%"} alignItems={"center"} gap={4}>
                <VacancyHero title={"Vacancy not available"} />
                <Stack width={{ xs: "90%", lg: "60%" }} marginX="auto" sx={{
                    border: `1px solid ${theme.palette.brand.border1.main}`,
                    borderRadius: 2,
                    backgroundColor: "#0F0F0F",
                    padding: 4,
                }} gap={2}>
                    <Typography variant="h5" component="h5" fontWeight={500}>
                        This listing has been taken down.
                    </Typography>
                    <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary}>
                        Please check our current openings.
                    </Typography>
                    <Stack width="fit-content">
                        <Link href="/vacancies" style={{ textDecoration: "none" }}>
                            <SideTabbedButton paddingX={18} hoverShiftX={1.5} hoverShiftY={-1}>
                                <div className="flex items-center justify-center gap-1">
                                    <Typography component="span" variant="h6" marginRight={0.5}>
                                        View vacancies
                                    </Typography>
                                    <span className="arrow-clip">
                                        <span className="arrow-slide">
                                            <Image
                                                src={arrow}
                                                alt="arrow animation"
                                                width={20}
                                                height={20}
                                                style={{ display: "block" }}
                                            />
                                        </span>
                                    </span>
                                </div>
                            </SideTabbedButton>
                        </Link>
                    </Stack>
                </Stack>
                <Stack width={"100%"} alignItems={"center"} position={"relative"} sx={{
                    borderTop: `1px solid ${theme.palette.brand.border1.main}`,
                    marginTop: 6,
                }}>
                    <Footer />
                </Stack>
            </Stack>
        );
    }


    return (
        <Stack width={"100%"} alignItems={"center"} gap={4}>
            <VacancyHero title={vacancy.title} />
            <Stack width={{ xs: "100%", md: "90%", lg: "60%" }} marginX="auto" sx={{
                borderTop: `1px solid ${theme.palette.brand.border1.main}`,
                borderBottom: `1px solid ${theme.palette.brand.border1.main}`,
                borderLeft: { xs: "none", md: `1px solid ${theme.palette.brand.border1.main}` },
                borderRight: { xs: "none", md: `1px solid ${theme.palette.brand.border1.main}` },
                borderRadius: { xs: 0, md: 2 },
                backgroundColor: { xs: "transparent", md: "#0F0F0F" },
                padding: { xs: 2, md: 4 },
            }}>
                {/* About this role (top metadata) */}
                <Stack gap={3}>
                    <Typography variant="h5" component="h5" fontWeight={500}>
                        About this role
                    </Typography>
                    <Stack gap={1}>
                        {Object.entries(vacancy.aboutThisRole).map(([key, value]) => (
                            <Stack key={key} direction="row" alignItems="flex-start">
                                <Stack direction="row" alignItems="flex-start" gap={1}>
                                    <Box sx={{
                                        width: 10,
                                        height: 10,
                                        flexShrink: 0,
                                        marginTop: "7px",
                                        backgroundColor: theme.palette.brand.mgsBlue2.main,
                                    }} />
                                    <Typography variant="body1" component="p" fontWeight={400} whiteSpace="nowrap">
                                        {aboutThisRoleLabel(key)}:&nbsp;
                                    </Typography>
                                </Stack>
                                <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary}>
                                    {String(value)}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>

                <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />

                {/* Section 2 - About Moods Studio */}
                <Stack gap={2}>
                    <Typography variant="h5" component="h5" fontWeight={500}>
                        About Moods Studio
                    </Typography>
                    <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary} sx={{ whiteSpace: "pre-line" }}>
                        {vacancy.thisIsMoodsStudio}
                    </Typography>
                </Stack>

                <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />

                {/* Section 3 - About the position (title is data-driven) */}
                <Stack gap={2}>
                    <Typography variant="h5" component="h5" fontWeight={500}>
                        {vacancy.whyJoinTeamTitle}
                    </Typography>
                    <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary} sx={{ whiteSpace: "pre-line" }}>
                        {vacancy.whyJoinTeam}
                    </Typography>
                </Stack>

                {/* Role-specific block, e.g. "Our stack" */}
                {vacancy.roleTitle && (
                    <>
                        <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />
                        <Stack gap={2}>
                            <Typography variant="h5" component="h5" fontWeight={500}>
                                {vacancy.roleTitle}
                            </Typography>
                            <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary} sx={{ whiteSpace: "pre-line" }}>
                                {vacancy.roleDescription}
                            </Typography>
                        </Stack>
                    </>
                )}

                <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />

                {/* Section 4 - What you will do */}
                <Stack gap={3}>
                    <Typography variant="h5" component="h5" fontWeight={500}>
                        What you will do
                    </Typography>
                    <Stack gap={1}>
                        {vacancy.whatYoullDo.map((item) => (
                            <Stack key={`${item.label}-${item.description}`} direction="row" alignItems="flex-start">
                                <Stack direction="row" alignItems="flex-start" gap={1}>
                                    <Box sx={{
                                        width: 10,
                                        height: 10,
                                        flexShrink: 0,
                                        backgroundColor: theme.palette.brand.mgsBlue2.main,
                                        marginTop: "7px",
                                    }} />
                                    <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary}>
                                        <Box component="span" fontWeight={600} color={theme.palette.text.primary}>
                                            {item.label}:
                                        </Box>{" "}
                                        {item.description}
                                    </Typography>
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>
                </Stack>

                <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />

                {/* Section 5 - What we are looking for */}
                <Stack gap={3}>
                    <Typography variant="h5" component="h5" fontWeight={500}>
                        What we are looking for
                    </Typography>

                    <Stack gap={1}>
                        {vacancy.whoYouAreCore.map((text) => (
                            <Stack key={text} direction="row" alignItems="flex-start">
                                <Stack direction="row" alignItems="flex-start" gap={1}>
                                    <Box sx={{ width: 10, height: 10, flexShrink: 0, marginTop: "7px", backgroundColor: theme.palette.brand.mgsBlue2.main }} />
                                    <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary}>
                                        {text}
                                    </Typography>
                                </Stack>
                            </Stack>
                        ))}
                    </Stack>

                </Stack>

                {/* Section 6 - Nice to have */}
                {(vacancy.whoYouAreNiceToHave?.length ?? 0) > 0 && (
                    <>
                        <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />
                        <Stack gap={3}>
                            <Typography variant="h5" component="h5" fontWeight={500}>
                                Nice to have
                            </Typography>
                            <Stack gap={1}>
                                {(vacancy.whoYouAreNiceToHave ?? []).map((text) => (
                                    <Stack key={text} direction="row" alignItems="flex-start">
                                        <Stack direction="row" alignItems="flex-start" gap={1}>
                                            <Box sx={{ width: 10, height: 10, flexShrink: 0, marginTop: "7px", backgroundColor: theme.palette.brand.mgsBlue2.main }} />
                                            <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary}>
                                                {text}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>
                        </Stack>
                    </>
                )}

                {/* Section 7 - Engagement, terms and compensation (and other role-specific blocks) */}
                {(vacancy.extraSections ?? []).map((section) => (
                    <Box key={section.title}>
                        <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />
                        <Stack gap={3}>
                            <Typography variant="h5" component="h5" fontWeight={500}>
                                {section.title}
                            </Typography>
                            {section.intro && (
                                <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary} sx={{ whiteSpace: "pre-line" }}>
                                    {section.intro}
                                </Typography>
                            )}
                            {(section.bullets?.length ?? 0) > 0 && (
                                <Stack gap={1}>
                                    {(section.bullets ?? []).map((item) => (
                                        <Stack key={`${item.label}-${item.description}`} direction="row" alignItems="flex-start">
                                            <Stack direction="row" alignItems="flex-start" gap={1}>
                                                <Box sx={{
                                                    width: 10,
                                                    height: 10,
                                                    flexShrink: 0,
                                                    backgroundColor: theme.palette.brand.mgsBlue2.main,
                                                    marginTop: "7px",
                                                }} />
                                                <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary}>
                                                    <Box component="span" fontWeight={600} color={theme.palette.text.primary}>
                                                        {item.label}:
                                                    </Box>{" "}
                                                    {item.description}
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </Box>
                ))}

                <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />

                {/* Section 8 - Equal opportunity */}
                <Stack gap={2}>
                    <Typography variant="h5" component="h5" fontWeight={500}>
                        Equal opportunity
                    </Typography>
                    <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary} sx={{ whiteSpace: "pre-line" }}>
                        {vacancy.deiCommitment}
                    </Typography>
                </Stack>

                {/* Section 9 - How your data is handled */}
                {vacancy.dataProtection && (
                    <>
                        <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />
                        <Stack gap={2}>
                            <Typography variant="h5" component="h5" fontWeight={500}>
                                How your data is handled
                            </Typography>
                            <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary} sx={{ whiteSpace: "pre-line" }}>
                                {vacancy.dataProtection}
                            </Typography>
                            <Link href="/privacy-policy" style={{ textDecoration: "underline", width: "fit-content" }}>
                                <Typography variant="body1" component="span" fontWeight={400} color={theme.palette.brand.mgsBlue2.main}>
                                    Read our privacy policy
                                </Typography>
                            </Link>
                        </Stack>
                    </>
                )}

                <Divider sx={{ borderBottom: `1px solid ${theme.palette.brand.border1.main}`, marginY: 5 }} />

                {/* Section 10 - Hiring process and what happens next */}
                <Stack gap={2}>
                    <Typography variant="h5" component="h5" fontWeight={500}>
                        Hiring process and what happens next
                    </Typography>
                    <Typography variant="body1" component="p" fontWeight={300} color={theme.palette.text.secondary} sx={{ whiteSpace: "pre-line" }}>
                        {vacancy.whatsNext}
                    </Typography>
                </Stack>
                <Divider sx={{
                    borderBottom: `1px solid ${theme.palette.brand.border1.main}`,
                    marginY: 5,
                }} />
                <Stack width="100%" height="100%" direction={{ xs: "column", lg: "row" }} alignItems="start" justifyContent="center" gap={2}>
                    <Stack width={{ xs: "100%", lg: "50%" }} gap={2}>
                        <Typography variant="h2" component="h2" className="chno" fontWeight={500}>
                            Apply for this position
                        </Typography>
                    </Stack>
                    <Stack width={{ xs: "100%", lg: "50%" }} gap={2} paddingTop={2}>
                        <InputBase
                            value={fullName}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setFullName(e.target.value)}
                            placeholder="Full Name"
                            type="text"
                            sx={{
                                width: "100%",
                                border: "1px solid #2F2F2F",
                                borderRadius: 2,
                                padding: 1,
                                background: "transparent",
                                color: theme.palette.text.primary,
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                },
                                "&:hover": {
                                    borderColor: "#FFFFFF",
                                }
                            }}
                        />
                        <InputBase
                            value={emailAddress}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEmailAddress(e.target.value)}
                            placeholder="Email Address"
                            type="email"
                            sx={{
                                width: "100%",
                                border: "1px solid #2F2F2F",
                                borderRadius: 2,
                                padding: 1,
                                background: "transparent",
                                color: theme.palette.text.primary,
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                },
                                "&:hover": {
                                    borderColor: "#FFFFFF",
                                }
                            }}
                        />
                        <InputBase
                            value={linkedinProfile}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setLinkedinProfile(e.target.value)}
                            placeholder="Linkedin Profile (URL)"
                            type="url"
                            sx={{
                                width: "100%",
                                border: "1px solid #2F2F2F",
                                borderRadius: 2,
                                padding: 1,
                                background: "transparent",
                                color: theme.palette.text.primary,
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                },
                                "&:hover": {
                                    borderColor: "#FFFFFF",
                                }
                            }}
                        />
                        <InputBase
                            value={portfolioUrl}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setPortfolioUrl(e.target.value)}
                            placeholder="Portfolio (URL)"
                            type="url"
                            sx={{
                                width: "100%",
                                border: "1px solid #2F2F2F",
                                borderRadius: 2,
                                padding: 1,
                                background: "transparent",
                                color: theme.palette.text.primary,
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                },
                                "&:hover": {
                                    borderColor: "#FFFFFF",
                                }
                            }}
                        />
                        <UploadField value={resumeFile} onChange={setResumeFile} label="Upload Resume" />
                        <UploadField value={coverLetterFile} onChange={setCoverLetterFile} label="Upload Cover Letter" />
                        {
                            submitOk ?
                                (
                                    <Typography variant="h6" textAlign="center" pt={2} lineHeight={1.6} color="success.main">
                                        Application submitted successfully!
                                        <br />
                                        <span style={{
                                            fontSize: 16,
                                            fontWeight: 400,
                                            opacity: 0.85
                                        }}>
                                            You will hear from us very soon.
                                        </span>
                                    </Typography>
                                )
                                :
                                (
                                    <Stack width="100%" direction={{ xs: "column", lg: "row" }} alignItems="center" justifyContent={{ xs: "center", lg: "space-between" }} mt={2} gap={{ xs: 2, lg: 0 }}>
                                        <Stack width={{ xs: "100%", lg: "fit-content" }} direction="row" alignItems="center" justifyContent={{ xs: "start", lg: "center" }} gap={1}>
                                            <Checkbox
                                                edge="start"
                                                checked={agreeToProcessing}
                                                onChange={() => setAgreeToProcessing(!agreeToProcessing)}
                                                tabIndex={-1}
                                                disableRipple
                                                sx={{
                                                    paddingRight: 0,
                                                    color: theme.palette.text.primary,
                                                    "&.Mui-checked": { color: theme.palette.text.primary },
                                                }}
                                            />
                                            <Typography component="span" variant="caption" fontWeight={300} color={theme.palette.text.secondary}>
                                                I agree to the processing of personal data
                                            </Typography>
                                        </Stack>
                                        <Stack display={{ xs: "none", lg: "block" }} width="fit-content">
                                            <Link href="" onClick={async (e: MouseEvent<HTMLAnchorElement>) => {
                                                e.preventDefault();
                                                if (!canSubmit) return;
                                                await onSubmit();
                                            }}>
                                                <SideTabbedButton paddingX={18} hoverShiftX={1.5} hoverShiftY={-1} disabled={!canSubmit} >
                                                    <div className="flex items-center justify-center gap-1">
                                                        <Typography component="span" variant="h6" marginRight={0.5}>
                                                            {isSubmitting ? "Sending..." : "Send"}
                                                        </Typography>
                                                        <span className="arrow-clip">
                                                            <span className="arrow-slide">
                                                                <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                                            </span>
                                                        </span>
                                                    </div>
                                                </SideTabbedButton>
                                            </Link>
                                        </Stack>
                                        <Stack display={{ xs: "block", lg: "none" }} width="100%" justifyContent="center" alignItems="center">
                                            <SideTabbedButton fullWidth paddingX={18} hoverShiftX={1.5} hoverShiftY={-1} disabled={!canSubmit} action={canSubmit ? onSubmit : undefined}>
                                                <div className="flex items-center justify-center gap-1">
                                                    <Typography component="span" variant="h6" marginRight={0.5}>
                                                        {isSubmitting ? "Sending..." : "Send"}
                                                    </Typography>
                                                    <span className="arrow-clip">
                                                        <span className="arrow-slide">
                                                            <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                                        </span>
                                                    </span>
                                                </div>
                                            </SideTabbedButton>
                                        </Stack>
                                    </Stack>
                                )
                        }
                        {submitError && (
                            <Typography variant="body2" color="error" textAlign="center" pt={2}>
                                {submitError}
                            </Typography>
                        )}
                    </Stack>
                </Stack>

            </Stack>
            <Stack width={"100%"} alignItems={"center"} position={"relative"} sx={{
                borderTop: `1px solid ${theme.palette.brand.border1.main}`,
                marginTop: 6,
            }}>
                <Footer />
            </Stack>
        </Stack>
    );
}
